import { getRouteApi } from '@tanstack/react-router';
import { ContextMenu, type ContextMenuItem } from 'cpool-components/general/ContextMenu';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { APPLICANT_ATTRIBUTIONS } from 'cpool-constants';
import { createId } from 'cpool-utils';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus } from 'react-icons/lu';

import { incomeAddButtonOrder, type IncomeType, incomeTypeConfig } from './incomeTypeConfig.ts';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaIncomeForm } from '~/features/leadDetails/schemas/income.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    applicant: 1 | 2;
};

export const AddIncome: React.FC<Props> = ({ applicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control } = useFormContext<SchemaIncomeForm>();
    const { registerAddedRow } = useLeadDetailsFormContext();
    const { t } = useTranslation();
    const { append: appendSpouseIncome } = useFieldArray({ control, name: 'ehegattenunterhalt' });
    const { append: appendCapitalIncome } = useFieldArray({ control, name: 'einkuenfteAusKapitalvermoegen' });
    const { append: appendSecondaryEmploymentIncome } = useFieldArray({
        control,
        name: 'einkuenfteAusNebentaetigkeit',
    });
    const { append: appendOtherIncome } = useFieldArray({ control, name: 'sonstigeEinnahmen' });
    const { append: appendPensionIncome } = useFieldArray({ control, name: 'unbefristeteZusatzrenten' });
    const attribution =
        applicant === 2 ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2 : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1;

    const appendIncome = (type: IncomeType) => {
        const base = {
            betragMonatlich: 0,
            gehoertZuAntragsteller: attribution,
            id: createId(),
        };

        switch (type) {
            case 'ehegattenunterhalt':
                appendSpouseIncome(base);
                break;
            case 'einkuenfteAusKapitalvermoegen':
                appendCapitalIncome(base);
                break;
            case 'einkuenfteAusNebentaetigkeit':
                appendSecondaryEmploymentIncome({ ...base, beginnDerTaetigkeit: null });
                break;
            case 'sonstigeEinnahmen':
                appendOtherIncome({ ...base, notiz: null });
                break;
            case 'unbefristeteZusatzrenten':
                appendPensionIncome(base);
                break;
        }

        registerAddedRow(base.id);
    };
    const menuItems: ContextMenuItem[] = incomeAddButtonOrder.map((type) => ({
        disabled: !canExecuteApplicantCommand({
            applicant,
            commandName: incomeTypeConfig[type].addCommand,
            permissions,
        }),
        id: type,
        label: t(incomeTypeConfig[type].label),
        onClick: () => appendIncome(type),
    }));

    return (
        <ContextMenu
            items={menuItems}
            maxHeight={incomeAddButtonOrder.length * 32 + 10}
            panelWidth={280}
            trigger={
                <Button
                    ariaLabel={t('LEADS.DETAILS.INCOME.ADD')}
                    disabled={menuItems.every((item) => item.disabled)}
                    className={'h-8 w-8 shrink-0'}
                    iconLeft={<LuPlus size={14} />}
                    type="button"
                    variant={ButtonVariant.SECONDARY}
                />
            }
        />
    );
};
